import { useState } from "react";
import { Link } from "react-router-dom";
import { WhatsappLogo, CreditCard, Copy, Warning, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { debtorApi } from "@/lib/api";
import { toast } from "sonner";
import Spinner from "@/components/Spinner";

export default function MessageGenerator({ debtor, bank, initialMessage, onAfterAction }) {
  const [message, setMessage] = useState(initialMessage || "");
  const [waPhone, setWaPhone] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [confirmFullPayment, setConfirmFullPayment] = useState(false);

  const handleGenerate = async () => {
    if (!bank) toast.warning("Configure primero sus datos bancarios en Recaudo");
    setGenLoading(true);
    try {
      const r = await debtorApi.generateMessage(debtor.id, "whatsapp");
      setMessage(r.message);
      setWaPhone(r.wa_phone);
      toast.success("Mensaje generado por el Estratega IA");
    } catch {
      toast.error("Error generando mensaje");
    } finally {
      setGenLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(message);
    toast.success("Mensaje copiado");
  };

  const openWhatsApp = () => {
    if (!message) return toast.warning("Genere el mensaje primero");
    const phone = waPhone || (debtor.telefono || "").replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
    toast.success(`Mensaje enviado a ${debtor.cliente_nombre}`);
    debtorApi.markStatus(debtor.id, "Contactado").then(onAfterAction).catch(() => {});
  };

  const confirmAndMarkPaid = () => {
    debtorApi
      .markStatus(debtor.id, "Pagado")
      .then(() => {
        toast.success("Deuda cerrada. Estado: Pagado · Saldo en $0.00");
        setConfirmFullPayment(false);
        onAfterAction?.();
      })
      .catch(() => toast.error("Error cerrando la deuda"));
  };

  return (
    <div className="space-y-4" data-testid="message-generator">
      {!bank && (
        <div
          className="flex items-start gap-3 p-4 border border-warning/50 bg-warning/10 rounded-md text-sm"
          data-testid="bank-missing-alert"
        >
          <Warning size={20} className="text-warning shrink-0 mt-0.5" weight="duotone" />
          <div className="flex-1">
            <div className="font-semibold text-warning-foreground">
              Complete su perfil bancario antes de enviar mensajes
            </div>
            <p className="text-muted-foreground mt-1">
              Aún no ha configurado sus datos bancarios. Sin estos datos, los mensajes saldrán
              incompletos y los deudores no sabrán a qué cuenta transferir.
            </p>
            <Link
              to="/recaudo"
              className="inline-flex items-center gap-1 mt-2 text-primary font-medium hover:underline"
              data-testid="goto-recaudo-link"
            >
              Ir a Recaudo Bancario →
            </Link>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-md p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="overline">Canal</div>
            <div className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#25D366]/10 text-[#128C7E]">
              <WhatsappLogo size={16} weight="fill" />
              <span className="text-sm font-medium">WhatsApp</span>
            </div>
          </div>
          <Button onClick={handleGenerate} disabled={genLoading} data-testid="generate-message-btn">
            {genLoading ? (
              <Spinner size="s" className="mr-2" />
            ) : (
              <Sparkle size={16} weight="duotone" className="mr-2" />
            )}
            {genLoading ? "Generando…" : message ? "Regenerar" : "Generar mensaje"}
          </Button>
        </div>

        <Textarea
          data-testid="message-textarea"
          className="ai-message min-h-[320px] font-mono text-sm leading-relaxed"
          placeholder="Haga clic en 'Generar mensaje' y el Estratega IA redactará el mensaje según el nivel de riesgo del deudor, incluyendo sus datos bancarios para el convenio de pago."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copy} disabled={!message} data-testid="copy-message-btn">
            <Copy size={16} className="mr-2" /> Copiar
          </Button>
          <Button
            onClick={openWhatsApp}
            disabled={!message}
            className="bg-[#25D366] hover:bg-[#25D366]/90 text-white"
            data-testid="send-whatsapp-btn"
          >
            <WhatsappLogo size={16} weight="fill" className="mr-2" /> Enviar WhatsApp
          </Button>
          <Button variant="secondary" onClick={() => setConfirmFullPayment(true)} data-testid="mark-fullpaid-btn">
            <CreditCard size={16} weight="duotone" className="mr-2" /> Marcar como pago completo
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmFullPayment} onOpenChange={setConfirmFullPayment}>
        <AlertDialogContent data-testid="fullpaid-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-heading">
              <Warning size={22} weight="duotone" className="text-warning" />
              Confirmar pago completo
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas cerrar esta deuda? Por favor, verifica que el pago total
              haya sido recibido en tu cuenta bancaria. Esta acción ajustará el saldo pendiente a
              <span className="font-mono font-semibold text-foreground"> $0.00 </span>
              y marcará al deudor como <span className="font-semibold text-foreground">Pagado</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fullpaid-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmAndMarkPaid(); }}
              data-testid="fullpaid-confirm"
              className="bg-success text-success-foreground hover:bg-success/90"
            >
              Sí, cerrar deuda
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
